import {FC, useMemo} from 'react';
import {Drawer} from 'shared/ui/drawer';
import {DiaryRow} from './types';
import {
  NewEntrieBody,
  NewEntrieBorder,
  NewEntrieFilesWrapper,
  NewEntrieFooter,
  NewEntrieHead,
  NewEntrieImage,
  NewEntrieRow,
  NewEntrieSubtitle,
  NewEntrieTitle,
  NewEntrieWrapper,
} from '../styles';
import Button from '@atomaro/ui-kit/components/Button/Button/Button';
import {CloseIcon} from 'shared/ui/icons';
import {toRem} from 'shared/helpers';
import TagItem from '@atomaro/ui-kit/components/Tag/TagItem/TagItem';
import TagGroup from '@atomaro/ui-kit/components/Tag/TagGroup/TagGroup';
import profileImg from 'shared/assets/img/user.png';
import {Spacer} from 'shared/ui/spacer';
import {format, formatDate} from 'date-fns';
import {TEvent} from 'entities/calendar';
import {notifySelectItems} from 'shared/consts';
import {Link} from 'react-router-dom';
import {LinkStyles} from './styles';
import {pickerDateToUtcIso, utcToUserDate} from 'features/calendar/helpers/dates.ts';

type DiaryDetailProps = {
  isOpen: boolean;
  row: DiaryRow | null;
  isMobile?: boolean;
  showFooter?: boolean;
  onClose: () => void;
  onEdit?: (row: DiaryRow) => void;
};

export const DiaryDetail: FC<DiaryDetailProps> = ({
  isOpen,
  row,
  isMobile,
  showFooter,
  onClose,
  onEdit,
}) => {
  const avatarImage = useMemo(() => {
    const avatarId = row?.author?.profile?.avatar?.id;
    if (!avatarId) {
      return profileImg;
    }

    return `/ecpp/attachments/files?attachmentId=${avatarId}`;
  }, [row]);

  const firstNotify = (event: TEvent) => {
    if (!event?.notifyAt) {
      return 'Нет';
    }
    const notifDate = new Date(event.notifyAt);
    const start = new Date(event.startAt);

    const diff = notifDate.getTime() - start.getTime();

    const finded = notifySelectItems.find((el) => el.key === diff);
    if (!finded) return 'Нет';

    return finded.value;
  };

  const secondNotify = (event: TEvent) => {
    if (!event?.notifyAtRepeat) {
      return 'Нет';
    }
    const notifDate = new Date(event.notifyAtRepeat);
    const start = new Date(event.startAt);

    const diff = notifDate.getTime() - start.getTime();

    const finded = notifySelectItems.find((el) => el.key === diff);
    if (!finded) return 'Нет';

    return finded.value;
  };

  return (
    <Drawer position="right" isOpen={isOpen} onClose={onClose}>
      <NewEntrieWrapper>
        <NewEntrieHead $isMobile={isMobile}>
          <Button
            iconPrefix={<CloseIcon />}
            variant={isMobile ? 'ghost' : 'secondary'}
            colorScheme="neutral"
            style={{
              alignSelf: 'flex-end',
              width: isMobile ? toRem(24) : toRem(36),
            }}
            size={isMobile ? 's' : 'm'}
            onClick={onClose}
          />
          <NewEntrieTitle>Детали записи</NewEntrieTitle>
          {isMobile && <Spacer width={toRem(24)} height={toRem(24)} />}
        </NewEntrieHead>
        <NewEntrieBody>
          {row && (
            <>
              {row.inspectedAt && (
                <NewEntrieRow>
                  <span>Дата осмотра</span>
                  <div>{formatDate(pickerDateToUtcIso(row.inspectedAt), 'dd.MM.yyyy')}</div>
                </NewEntrieRow>
              )}
              {row.hiveNumber && (
                <NewEntrieRow>
                  <span>Номер улья</span>
                  <LinkStyles>
                    <Link to={`/hive/${row.hiveId}`}>№ {row.hiveNumber}</Link>
                  </LinkStyles>
                </NewEntrieRow>
              )}
              {row.author && (
                <NewEntrieRow>
                  <span>Сотрудник</span>
                  <TagItem>
                    <img src={avatarImage} alt={row.author.name.first} />
                    <div>{row.author.name.first}</div>
                  </TagItem>
                </NewEntrieRow>
              )}
              {(row.inspectedAt || row.hiveNumber || row.author) && <NewEntrieBorder />}
              {row.frames && (
                <NewEntrieRow>
                  <span>Всего рамок</span>
                  <div>{row.frames}</div>
                </NewEntrieRow>
              )}
              {row.strength && (
                <NewEntrieRow>
                  <span>Сила семьи</span>
                  <div>{row.strength}</div>
                </NewEntrieRow>
              )}
              {row.brood && (
                <NewEntrieRow>
                  <span>Расплод</span>
                  <div>{row.brood}</div>
                </NewEntrieRow>
              )}

              {(row.frames || row.strength || row.brood) && <NewEntrieBorder />}

              {Boolean(row.dictionaries?.length) && (
                <>
                  <NewEntrieRow $alignItems="flex-start">
                    <span>Состояние семьи</span>
                    <TagGroup
                      items={row.dictionaries.map((activeDictionary) => ({
                        value: activeDictionary.name,
                        key: activeDictionary.id,
                      }))}
                    />
                  </NewEntrieRow>

                  <NewEntrieBorder />
                </>
              )}

              {row.note && (
                <NewEntrieRow $alignItems="flex-start">
                  <span>Комментарий</span>
                  <div>{row.note}</div>
                </NewEntrieRow>
              )}

              {Boolean(row.attachments?.length) && (
                <NewEntrieRow $alignItems="flex-start">
                  <span>
                    Вложения <i>{row.attachments.length}</i>
                  </span>
                  <NewEntrieFilesWrapper>
                    {row.attachments.map((attachment, index) => (
                      <NewEntrieImage key={index}>
                        <img
                          src={`/ecpp/attachments/files?attachmentId=${attachment.id}`}
                          alt={`attachment_${attachment.id}`}
                        />
                      </NewEntrieImage>
                    ))}
                  </NewEntrieFilesWrapper>
                </NewEntrieRow>
              )}

              {row.fields?.map((field) => (
                <NewEntrieRow>
                  <span>{field.name}</span>
                  {field.type === 'DICTIONARY' && field.dictionaries && (
                    <TagGroup
                      items={field.dictionaries.map((activeDictionary) => ({
                        value: activeDictionary.name,
                        key: activeDictionary.id,
                      }))}
                    />
                  )}
                  {field.type === 'DATE' && field.value && (
                    <div>{formatDate(field.value, 'dd.MM.yyyy')}</div>
                  )}
                  {field.type !== 'DATE' && field.type !== 'DICTIONARY' && <div>{field.value}</div>}
                </NewEntrieRow>
              ))}

              {row.events &&
                row.events?.length > 0 &&
                row.events.map((event) => (
                  <>
                    <NewEntrieSubtitle>Напоминание</NewEntrieSubtitle>
                    <NewEntrieRow>
                      <span>Описание</span>
                      <div>{event.description}</div>
                    </NewEntrieRow>
                    <NewEntrieRow>
                      <span>Начало</span>
                      <div>{format(utcToUserDate(event.startAt), 'dd.MM.yyyy, HH:mm')}</div>
                    </NewEntrieRow>
                    <NewEntrieRow>
                      <span>Окончание</span>
                      <div>{format(utcToUserDate(event.endAt), 'dd.MM.yyyy, HH:mm')}</div>
                    </NewEntrieRow>
                    <NewEntrieRow>
                      <span>Напоминание</span>
                      <div>{firstNotify(event)}</div>
                    </NewEntrieRow>
                    <NewEntrieRow>
                      <span>2-ое напоминание</span>
                      <div>{secondNotify(event)}</div>
                    </NewEntrieRow>
                  </>
                ))}
            </>
          )}
        </NewEntrieBody>
        {onEdit && row && showFooter && (
          <NewEntrieFooter>
            <Button
              label="Редактировать"
              onClick={() => onEdit(row)}
              style={isMobile ? {width: '100%'} : {}}
            />
          </NewEntrieFooter>
        )}
      </NewEntrieWrapper>
    </Drawer>
  );
};
